// src/components/FeatureTour.tsx
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  image?: string;
  position: 'center' | 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    title: '👋 Welcome to WHIPT!',
    description: 'Where\'d I Put That? - Your academic organizer that helps you never lose track of assignments, meetings, or documents again!',
    position: 'center'
  },
  {
    title: '📚 Organize Your Classes',
    description: 'Create multiple class notebooks to keep all your coursework organized. Each class has categories for Discussions, Assignments, Experiential Learning, and Meetings.',
    position: 'center'
  },
  {
    title: '💬 WHIPT Convo\'s',
    description: 'Stay connected with classmates! Click the communication panel to see who\'s online and start direct messages or join WHIPT Rooms for group discussions.',
    position: 'left'
  },
  {
    title: '🎯 WHIPT Rooms',
    description: 'Create custom chat rooms for different purposes: study groups, project teams, board meetings, and more. Each room is color-coded and tracks unread messages.',
    position: 'center'
  },
  {
    title: '📎 Share Files Instantly',
    description: 'Upload and share files directly in chats. Click the paperclip icon to attach images, PDFs, or documents. Others can view them instantly!',
    position: 'center'
  },
  {
    title: '🎥 Virtual Rooms',
    description: 'Host video meetings with classmates. Features include screen sharing, live chat, AI transcription, and even food ordering without leaving the session!',
    position: 'center'
  },
  {
    title: '🎵 Study with Music',
    description: 'Access curated playlists while you work. Click the music player on the right to choose from lo-fi, ambient, and study beats.',
    position: 'right'
  },
  {
    title: '🔒 Privacy Controls',
    description: 'Control your online status with the toggle in the header. Appear online to collaborate or offline when you need to focus.',
    position: 'top'
  },
  {
    title: '🍕 Late Night Snacks',
    description: 'Order food while studying! The Snack Dial connects you with local restaurants. Available in the organizer and during virtual meetings.',
    position: 'center'
  },
  {
    title: '📅 Never Miss a Deadline',
    description: 'Add due dates to assignments and view them all in the calendar. Set reminders so you\'re always on track.',
    position: 'center'
  },
  {
    title: '✨ You\'re All Set!',
    description: 'Start organizing your academic life with WHIPT. Remember: you can reopen this tour anytime from the settings menu.',
    position: 'center'
  }
];

export function FeatureTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Only show tour if user hasn't completed it yet
    const tourCompleted = localStorage.getItem('whipt_tour_completed');
    if (!tourCompleted) {
      setIsOpen(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('whipt_tour_completed', 'true');
    setIsOpen(false);
    setCurrentStep(0);
  };

  const handleSkip = () => {
    localStorage.setItem('whipt_tour_completed', 'true');
    setIsOpen(false);
    setCurrentStep(0);
  };

  if (!isOpen) return null;

  const currentTourStep = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="relative w-full max-w-2xl">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute -top-12 right-0 text-white hover:text-amber-300 flex items-center gap-2 transition-colors"
        >
          <span className="text-sm">Skip Tour</span>
          <X className="w-6 h-6" />
        </button>

        {/* Tour card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl border-2 border-amber-400/30 shadow-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-slate-700">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-3">
                {currentTourStep.title}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {currentTourStep.description}
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {TOUR_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-amber-500'
                      : index < currentStep
                      ? 'w-2 bg-amber-500/50'
                      : 'w-2 bg-slate-600'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Previous</span>
              </button>

              <span className="text-sm text-slate-400">
                Step {currentStep + 1} of {TOUR_STEPS.length}
              </span>

              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg hover:shadow-amber-500/50 transition-all"
              >
                {currentStep === TOUR_STEPS.length - 1 ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Get Started</span>
                  </>
                ) : (
                  <>
                    <span>Next</span>
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Restart tour hint */}
        <p className="text-center text-xs text-slate-400 mt-4">
          You can restart this tour anytime from Settings → Help & Tour
        </p>
      </div>
    </div>
  );
}

// Export function to restart tour
export function restartTour() {
  localStorage.removeItem('whipt_tour_completed');
  window.location.reload();
}
